import React, { useCallback, useEffect, useState } from 'react';

import Link from '@dashboard/components/mui-atoms/Link';
import { type FWorkflowEventFragment } from '@dashboard/generated/wp/platform';
import { Box, CircularProgress, List, ListItem, ListItemButton, Stack, Typography } from '@mui/material';

import { DateFormatter } from '@waypoint/react-formatters';

import { useCustomPreviewData } from '../../FormContext';

type Props = {
  loading: boolean;
  workflowRuns: FWorkflowEventFragment[];
};
export default function TriggerHistoryResults({ loading, workflowRuns }: Props) {
  const [currentRow, setCurrentRow] = useState<FWorkflowEventFragment | null>(null);
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const [_, setValue] = useCustomPreviewData();

  const onRowClick = useCallback(
    (fragment: FWorkflowEventFragment) => {
      setValue(JSON.stringify(fragment.variables, null, '  '));
      setCurrentRow(fragment);
    },
    [setValue, setCurrentRow]
  );

  useEffect(() => {
    if (loading) {
      return;
    }
    if (currentRow === null && workflowRuns.length > 0) {
      onRowClick(workflowRuns[0]);
    }
  }, [loading, workflowRuns, currentRow, onRowClick]);

  if (loading) {
    return (
      <Stack direction="row" p={2} spacing={1.5}>
        <CircularProgress size={16} />
        <Typography variant="body2">Checking for requests &hellip;</Typography>
      </Stack>
    );
  }

  if (workflowRuns.length === 0) {
    return (
      <Box p={2}>
        <Typography variant="body2" color="text.secondary">
          No triggers have been sent to this workflow yet &mdash;{' '}
          <Link
            color="inherit"
            fontWeight="fontWeightMedium"
            underline="hover"
            href="https://www.usewaypoint.com/docs/triggering-workflows"
            target="_blank"
          >
            learn more
          </Link>
        </Typography>
      </Box>
    );
  }
  return (
    <List sx={{ py: 0 }}>
      {workflowRuns.map((event) => (
        <ListItem disablePadding>
          <ListItemButton
            selected={event === currentRow}
            sx={{ p: 1.5, pl: 2, pr: 4 }}
            onClick={() => onRowClick(event)}
          >
            <Typography variant="body2">
              <DateFormatter value={event.createdAt} template="full" />
            </Typography>
          </ListItemButton>
        </ListItem>
      ))}
    </List>
  );
}
